// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { NavigateFunction } from "react-router-dom";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { MainPanel } from "types";
import { isSet } from "shared/utils/utils";
import { ActionType, IAction, raiseAction } from "actions";
import { tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { IOcppChargingSession, IRfid } from "states/ocppstates";

import FormattedTime from "./FormattedTime";
import Rfid from "model/rfid";
import SearchFilter from "./SearchFilter";

interface IRfidListExtProps {
  isDashboard: boolean;
  showOnlyCharging?: boolean;
}

interface IRfidListProps {
  selectedRfid: number | undefined;
  rfidFilter: string | undefined;
  rfids: IRfid[];
  pendingSession: IOcppChargingSession[];
}

const mapStateToProps = (state: IState) => {
  const props: IRfidListProps = {
    rfids: state.ocpp.rfid,
    selectedRfid: state.router.rfid,
    rfidFilter: state.router.rfidFilter,
    pendingSession: state.ocpp.pendingChargingSession,
  };
  return props;
};

interface IRfidListDispatch {
  onSelectRfid: (rfid: number | undefined, navigate: NavigateFunction) => void;
  onSelectFilter: (filterText: string, navigate: NavigateFunction) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidListDispatch = {
    onSelectRfid: (rfidId: number | undefined, navigate: NavigateFunction) => {
      if (isSet(rfidId)) {
        navigate(`../${MainPanel.RFIDDETAIL}?rfid=${rfidId}`);
      } else {
        navigate(`../${MainPanel.RFIDDETAIL}`);
      }
    },
    onSelectFilter: (filterText: string, navigate: NavigateFunction) => {
      navigate(`../${MainPanel.RFIDDETAIL}?rfidFilter=${encodeURIComponent(filterText)}`);
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  placeholderSearchField: {
    id: "Search for cards...",
  },
  tootTipSearchField: {
    id: "Search for cards-numbers, names or categories. To search for multiple fields, separate search texts by a space!",
  },
});

interface IRfidListState {
  filterText: string;
}

class RfidListComponent extends React.Component<
  IRfidListProps & IRfidListExtProps & IRfidListDispatch & RoutedProps,
  IRfidListState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IRfidListState = {
    // restore search text of selected filter, e.g. on page reload
    filterText: this.props.rfidFilter ?? "",
  };

  public render() {
    const rfidIndicis = Array.from(Array(this.props.rfids.length).keys())
      .filter((curIndex) => this.props.rfids[curIndex].isActive)
      .sort((a, b) => {
        const keyA =
          this.props.rfids[a]?.name ? this.props.rfids[a]?.name : this.props.rfids[a]?.rfid;
        const keyB =
          this.props.rfids[b]?.name ? this.props.rfids[b]?.name : this.props.rfids[b]?.rfid;
        return isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      });
    // names used by several cards; for these, we also show the card number
    const names = rfidIndicis.map((curIndex) => this.props.rfids[curIndex].name).filter(isSet);
    const duplicateNames = new Set(names.filter((name, index) => names.indexOf(name) !== index));
    const rfidItems = rfidIndicis
      .filter((rfid, rfidIndex) => {
        const pendingSession = this.props.pendingSession.find(
          (aSession) => this.props.rfids[rfid].rfid === aSession.rfid,
        );
        return (
          (this.props.showOnlyCharging !== true || isSet(pendingSession)) &&
          new Rfid(this.props.rfids[rfid]).matchesFilter(this.state.filterText)
        );
      })
      .map((curIndex) =>
        this.renderRfid(curIndex, duplicateNames.has(this.props.rfids[curIndex].name ?? "")),
      );
    const isAllSelected =
      this.props.selectedRfid === undefined && this.props.rfidFilter === undefined;
    const isFilterSelected =
      this.props.selectedRfid === undefined && this.props.rfidFilter === this.state.filterText;

    if (this.props.rfids.length === 0) {
      return <></>;
    }

    return (
      <>
        {this.props.isDashboard === false && (
          <SearchFilter
            className="tableSearchBoxSidebar"
            filterText={this.state.filterText}
            placeholder={this.context.formatMessage(messages.placeholderSearchField)}
            tooltip={this.context.formatMessage(messages.tootTipSearchField)}
            onChange={(filterText) => {
              this.setState({ filterText });
            }}
          />
        )}
        <div id="rfidlist">
          <div id="rfidlistframe">
            <table>
              <thead>
                <tr key="norfid" className="all-sessions">
                  <td colSpan={3} className="session-select">
                    <div>
                      <div
                        className={`link${isAllSelected ? " selected" : ""}`}
                        tabIndex={0}
                        onKeyDown={tabClickHandler}
                        onClick={() => this.props.onSelectRfid(undefined, this.props.navigate)}
                      >
                        <FormattedMessage id="show all charging sessions" />
                      </div>
                      {this.props.isDashboard === false &&
                        this.state.filterText !== "" &&
                        rfidItems.length > 0 && (
                          <div
                            className={`link${isFilterSelected ? " selected" : ""}`}
                            tabIndex={0}
                            onKeyDown={tabClickHandler}
                            onClick={() =>
                              this.props.onSelectFilter(this.state.filterText, this.props.navigate)
                            }
                          >
                            <FormattedMessage id="show filtered cards" />
                          </div>
                        )}
                    </div>
                  </td>
                </tr>
              </thead>
              <tbody>{rfidItems}</tbody>
            </table>
            {rfidItems.length === 0 && (
              <div>
                {this.props.showOnlyCharging ?
                  <FormattedMessage id="No charging card with active charging session found." />
                : <FormattedMessage id="No charging card found." />}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  private renderRfid(rfidIndex: number, showRfidNumber: boolean) {
    const rfid = this.props.rfids[rfidIndex];
    const pendingSession = this.props.pendingSession.find((curItem) => curItem.rfid === rfid.rfid);
    const className = ["link"];
    if (rfid.id === this.props.selectedRfid) {
      className.push("selected");
    }

    // render rfid, and potentially connector rows
    return (
      <tr
        key={rfidIndex}
        className={className.join(" ")}
        tabIndex={0}
        onKeyDown={tabClickHandler}
        onClick={() => this.props.onSelectRfid(rfid.id, this.props.navigate)}
      >
        <td className="status-circle">{this.renderStatusIcon(rfid, pendingSession)}</td>
        <td>
          {rfid.name ? rfid.name : rfid.rfid}
          {showRfidNumber && <span className="rfid-number ms-2">{rfid.rfid}</span>}
        </td>
        <td className="rfid-status">
          {pendingSession ?
            <>
              <FormattedMessage id="Charging since" />:{" "}
              <FormattedTime valueMs={pendingSession.startTimestamp * 1000} />
            </>
          : <></>}
        </td>
      </tr>
    );
  }

  private renderStatusIcon(rfid: IRfid, pendingSession: IOcppChargingSession | undefined) {
    const color = new Rfid(rfid, pendingSession).getColor();
    return (
      <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }}></div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RfidListComponent));
